"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MarketTradingProcessor = void 0;
const TradeAPILib_1 = require("TradeAPILib");
const trade_utils_lib_1 = require("trade_utils_lib");
const react_1 = require("react");
const SimpleTrading_1 = require("../SimpleTrading");
const operators_1 = require("rxjs/operators");
exports.MarketTradingProcessor = {
    useUserParams(market, symbol, baseBalance, quoteBalance, side, quantity, baseTotalBalance, currentPrice) {
        const orderType = (0, react_1.useMemo)(() => {
            const supportMarket = TradeAPILib_1.MARKET_ORDER_SUPPORT_DESC[market].support.indexOf(TradeAPILib_1.ExchangeOrderType.market) >= 0;
            return supportMarket ? TradeAPILib_1.ExchangeOrderType.market : TradeAPILib_1.ExchangeOrderType.limit;
        }, [market]);
        const [buyType, sellType] = (0, react_1.useMemo)(() => {
            if (market === TradeAPILib_1.ExchangeID.PIONEXV2) {
                return ["cost", "sell"];
            }
            else {
                return ["buy", "sell"];
            }
        }, [market]);
        const precision = (0, react_1.useMemo)(() => {
            return buyType === "cost" ? symbol.precisionCost : symbol.precision;
        }, [buyType, symbol]);
        const [baseAva, quoAva] = (0, react_1.useMemo)(() => {
            return [
                trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(baseBalance, symbol.amount),
                trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(quoteBalance, precision),
            ];
        }, [precision, baseBalance, quoteBalance, symbol]);
        const tradeType = (0, react_1.useMemo)(() => {
            return side === TradeAPILib_1.ExchangeOrderSide.buy ? buyType : sellType;
        }, [buyType, sellType, side]);
        const curPrice = (0, react_1.useMemo)(() => {
            if (orderType === TradeAPILib_1.ExchangeOrderType.limit) {
                if (side === TradeAPILib_1.ExchangeOrderSide.buy) {
                    return trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(new trade_utils_lib_1.SafeDecimal(currentPrice).mul(1.05), symbol.precision);
                }
                else {
                    return trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(new trade_utils_lib_1.SafeDecimal(currentPrice).mul(0.95), symbol.precision);
                }
            }
            else {
                return trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(currentPrice, symbol.precision);
            }
        }, [currentPrice, symbol, side]);
        return {
            market,
            symbol,
            side,
            baseBalance: baseAva,
            quoteBalance: quoAva,
            baseTotalBalance: baseTotalBalance,
            curPrice,
            quantity,
            orderType,
            tradeType,
        };
    },
    getCreateOrderRequest(params, $st) {
        var _a, _b, _c, _d;
        const { side, symbol, orderType, quantity, tradeType, curPrice, baseBalance, quoteBalance, baseTotalBalance } = params;
        const quantityDecimal = new trade_utils_lib_1.SafeDecimal(quantity);
        const priceDecimal = new trade_utils_lib_1.SafeDecimal(curPrice);
        const baseAva = new trade_utils_lib_1.SafeDecimal(baseBalance);
        const quoteAva = new trade_utils_lib_1.SafeDecimal(quoteBalance);
        // 判断余额是否足够
        if (tradeType === "buy") {
            if (Number(curPrice) && quantityDecimal.greaterThan(new trade_utils_lib_1.SafeDecimal(quoteBalance).div(priceDecimal))) {
                throw $st("trade_error_balance_not_enough");
            }
        }
        else if (tradeType === "cost") {
            if (quantityDecimal.greaterThan(quoteAva)) {
                throw $st("trade_error_balance_not_enough");
            }
            if (params.leverageCoinInfo) {
                const currentLeveragePosition = new trade_utils_lib_1.SafeDecimal(baseTotalBalance).mul(params.curPrice).add(quantityDecimal);
                if (quantityDecimal.greaterThan(params.leverageCoinInfo.quota)) {
                    throw $st("trade_cost_than_leverage_amount", { amount: params.leverageCoinInfo.quota });
                }
                else if (currentLeveragePosition.greaterThan(params.leverageCoinInfo.accountQuota)) {
                    throw $st("trade_cost_than_leverage_pair_limit", { amount: params.leverageCoinInfo.accountQuota });
                }
            }
        }
        else if (tradeType === "sell") {
            if (quantityDecimal.greaterThan(baseAva)) {
                throw $st("trade_error_balance_not_enough");
            }
        }
        // 判断参数是否正规
        if (params.market === TradeAPILib_1.ExchangeID.PIONEXV2) {
            let spending = undefined;
            let amount = undefined;
            if (side === TradeAPILib_1.ExchangeOrderSide.buy) {
                const spendingDecimal = trade_utils_lib_1.NumberCommonUtils.formatterDecimal(quantityDecimal, symbol.precisionCost);
                if (((_a = symbol.limit) === null || _a === void 0 ? void 0 : _a.spending.max) && spendingDecimal.greaterThan(symbol.limit.spending.max)) {
                    throw $st("trade_error_high_cost", { cost: symbol.limit.spending.max });
                }
                else if (((_b = symbol.limit) === null || _b === void 0 ? void 0 : _b.spending.min) && spendingDecimal.lessThan(symbol.limit.spending.min)) {
                    throw $st("trade_error_low_cost", { cost: symbol.limit.spending.min });
                }
                spending = spendingDecimal.toString();
                if (!spending) {
                    throw $st("trade_error_input_invest");
                }
            }
            else {
                const amountDecimal = trade_utils_lib_1.NumberCommonUtils.formatterDecimal(quantityDecimal, symbol.amount);
                if (((_c = symbol.limit) === null || _c === void 0 ? void 0 : _c.dumping.max) && amountDecimal.greaterThan(symbol.limit.dumping.max)) {
                    throw $st("trade_error_high_amount", { amount: symbol.limit.dumping.max });
                }
                else if (((_d = symbol.limit) === null || _d === void 0 ? void 0 : _d.dumping.min) && amountDecimal.lessThan(symbol.limit.dumping.min)) {
                    throw $st("trade_error_low_amount", { amount: symbol.limit.dumping.min });
                }
                amount = amountDecimal.toString();
                if (!amount) {
                    throw $st("trade_error_input_quantity");
                }
            }
            const request = {
                base: symbol.base,
                quote: symbol.quote,
                side: side,
                type: orderType,
                spending: spending,
                amount: amount,
            };
            return { marketParam: request };
        }
        else {
            let price = priceDecimal.toNumber();
            if (orderType === TradeAPILib_1.ExchangeOrderType.limit) {
                if (side === TradeAPILib_1.ExchangeOrderSide.buy) {
                    price = trade_utils_lib_1.NumberCommonUtils.formatterDecimal(priceDecimal.mul(1.05), symbol.precision).toNumber();
                }
                else {
                    price = trade_utils_lib_1.NumberCommonUtils.formatterDecimal(priceDecimal.mul(0.95), symbol.precision).toNumber();
                }
            }
            const min = SimpleTrading_1.SimpleTrading.getMinAmount(symbol, price);
            const max = SimpleTrading_1.SimpleTrading.getMaxAmount(symbol, price);
            if (min && quantityDecimal.lessThan(min)) {
                throw $st("trade_error_low_amount", { amount: min });
            }
            else if (max && quantityDecimal.greaterThan(max)) {
                throw $st("trade_error_high_amount", { amount: max });
            }
            if (!quantityDecimal.toNumber()) {
                throw $st("trade_error_input_quantity");
            }
            const request = {
                base: symbol.base,
                quote: symbol.quote,
                side: side,
                type: orderType,
                price: price,
                amount: quantityDecimal.toNumber(),
            };
            return { customParam: request };
        }
    },
    queryBuyMaxAmountToAdvice(asks, curPrice, maxOffsetRate) {
        const maxOffsetRateD = new trade_utils_lib_1.SafeDecimal(maxOffsetRate);
        const curPriceD = new trade_utils_lib_1.SafeDecimal(curPrice);
        let maxAmount = new trade_utils_lib_1.SafeDecimal(0); // 建议最大买入量
        let offsetRate = new trade_utils_lib_1.SafeDecimal(maxOffsetRate); // (建议最大买入量-当年价格)/当年价格
        if (asks.length > 0) {
            // 最大建议买价 当前价格 + 当前价格 * 最大差价比率
            const limitPrice = curPriceD.add(curPriceD.mul(maxOffsetRateD));
            // 卖盘价格为升序 计算建议最大买入量
            for (let i = 0; i < asks.length; i++) {
                const item = asks[i];
                if (new trade_utils_lib_1.SafeDecimal(item.price).lessThanOrEqualTo(limitPrice)) {
                    maxAmount = maxAmount.add(new trade_utils_lib_1.SafeDecimal(item.quantity));
                }
                else {
                    break;
                }
            }
            const lastPrice = new trade_utils_lib_1.SafeDecimal(asks[asks.length - 1].price);
            if (lastPrice.lessThan(limitPrice)) {
                const realRate = lastPrice.sub(curPriceD).div(curPriceD);
                offsetRate = trade_utils_lib_1.NumberCommonUtils.formatterDecimal(realRate, 4);
            }
        }
        return {
            maxAmount: maxAmount.toNumber(),
            offsetRate: offsetRate.toNumber(),
        };
    },
    querySellMaxAmountToAdvice(bids, curPrice, maxOffsetRate) {
        const maxOffsetRateD = new trade_utils_lib_1.SafeDecimal(maxOffsetRate);
        const curPriceD = new trade_utils_lib_1.SafeDecimal(curPrice);
        let maxAmount = new trade_utils_lib_1.SafeDecimal(0); // 建议最大买入量
        let offsetRate = new trade_utils_lib_1.SafeDecimal(maxOffsetRate); // (建议最大买入量-当年价格)/当年价格
        if (bids.length > 0) {
            // 最大建议卖价 当前价格 - 当前价格 * 最大差价比率
            const limitPrice = curPriceD.sub(curPriceD.mul(maxOffsetRateD));
            // 买盘价格为降序
            for (let i = 0; i < bids.length; i++) {
                const item = bids[i];
                if (new trade_utils_lib_1.SafeDecimal(item.price).greaterThanOrEqualTo(limitPrice)) {
                    maxAmount = maxAmount.add(new trade_utils_lib_1.SafeDecimal(item.quantity));
                }
                else {
                    break;
                }
            }
            const lastPrice = new trade_utils_lib_1.SafeDecimal(bids[bids.length - 1].price);
            if (lastPrice.greaterThan(limitPrice)) {
                const realRate = curPriceD.sub(lastPrice).div(curPriceD);
                offsetRate = trade_utils_lib_1.NumberCommonUtils.formatterDecimal(realRate, 4);
            }
        }
        return {
            maxAmount: maxAmount.toNumber(),
            offsetRate: offsetRate.toNumber(),
        };
    },
    queryWarningMaxAmount(symbol, curPrice, maxOffsetRate, side, api) {
        return api
            .getDepthDataObservable(symbol.base, symbol.quote)
            .pipe((0, operators_1.filter)((data) => {
            if (data) {
                return data.base === symbol.base && data.quote === symbol.quote;
            }
            return false;
        }), (0, operators_1.map)((data) => {
            return data;
        }), (0, operators_1.take)(1), (0, operators_1.timeout)(1500))
            .pipe((0, operators_1.map)((data) => {
            if (side === TradeAPILib_1.ExchangeOrderSide.buy && data.asks && data.asks.length > 0) {
                return exports.MarketTradingProcessor.queryBuyMaxAmountToAdvice(data.asks, curPrice, maxOffsetRate);
            }
            else if (side === TradeAPILib_1.ExchangeOrderSide.sell && data.bids && data.bids.length > 0) {
                return exports.MarketTradingProcessor.querySellMaxAmountToAdvice(data.bids, curPrice, maxOffsetRate);
            }
            return {
                maxAmount: 0,
                offsetRate: maxOffsetRate,
            };
        }));
    },
};
